import {
  ClipboardList,
  Building2,
  SearchCheck,
  Network,
  Server,
  ShieldCheck,
  AlertTriangle,
  FileSignature,
  FolderKanban,
  type LucideIcon,
} from "lucide-react";

export interface ServiceData {
  slug: string;
  title: string;
  shortTitle: string;
  icon: LucideIcon;
  description: string;
  overview: string;
  approach: string[];
  focusAreas: string[];
  frameworks: string[];
}

export const services: ServiceData[] = [
  {
    slug: "requirements-management",
    title: "Requirements Management",
    shortTitle: "Requirements",
    icon: ClipboardList,
    description:
      "Capturing, analyzing, and managing stakeholder requirements across the acquisition lifecycle.",
    overview:
      "Effective requirements management is the foundation of successful program execution. RCG helps government agencies capture, trace, and validate requirements from concept through deployment, ensuring every stakeholder need is addressed and every dollar is accounted for.",
    approach: [
      "Stakeholder elicitation workshops and structured interviews",
      "Requirements decomposition and traceability matrix development",
      "Change management and impact analysis across baselines",
      "Verification and validation planning tied to each requirement",
    ],
    focusAreas: [
      "Stakeholder needs analysis",
      "Requirements traceability",
      "Baseline management",
      "Change impact assessment",
      "Acceptance criteria definition",
    ],
    frameworks: ["DoD Architecture Framework (DoDAF)", "INCOSE SE Handbook", "ISO/IEC 29148", "CMMI for Development"],
  },
  {
    slug: "architecture-engineering",
    title: "Architecture Engineering",
    shortTitle: "Architecture",
    icon: Building2,
    description:
      "Designing enterprise and solution architectures aligned to DoD/IC frameworks like DoDAF and TOGAF.",
    overview:
      "RCG architects design resilient, scalable enterprise and solution architectures that align mission objectives with technical capabilities. Our team translates operational needs into actionable blueprints that guide development, integration, and sustainment.",
    approach: [
      "Current-state and target-state architecture development",
      "Gap analysis and migration roadmap planning",
      "Architecture governance and standards enforcement",
      "Model-based systems engineering (MBSE) integration",
    ],
    focusAreas: [
      "Enterprise architecture development",
      "Solution architecture design",
      "Technology roadmapping",
      "Architecture governance",
      "Cloud migration planning",
    ],
    frameworks: ["DoDAF", "TOGAF", "FEAF", "NIST SP 800-series", "Zero Trust Architecture"],
  },
  {
    slug: "independent-verification-validation",
    title: "Independent Verification & Validation",
    shortTitle: "IV&V",
    icon: SearchCheck,
    description:
      "Independent assessment of systems to ensure compliance with requirements and standards.",
    overview:
      "RCG provides unbiased, independent verification and validation services that give program managers confidence their systems meet requirements, perform as expected, and comply with applicable standards—before they reach the warfighter.",
    approach: [
      "Independent test planning and execution",
      "Requirements-based verification against baselines",
      "Risk-driven validation prioritization",
      "Deficiency reporting and corrective action tracking",
    ],
    focusAreas: [
      "Test strategy and planning",
      "Functional and performance verification",
      "Compliance auditing",
      "Regression testing",
      "Operational test support",
    ],
    frameworks: ["IEEE 1012 (V&V Standard)", "DoDI 5000.89", "ISO/IEC 12207", "MIL-STD-882E"],
  },
  {
    slug: "systems-integration",
    title: "Systems Integration",
    shortTitle: "Integration",
    icon: Network,
    description:
      "Connecting disparate systems into cohesive, interoperable solutions for complex environments.",
    overview:
      "Modern defense operations depend on seamless data flow across platforms, networks, and security domains. RCG integrates disparate systems—legacy and modern—into cohesive, interoperable solutions that enhance operational effectiveness.",
    approach: [
      "Interface analysis and protocol mapping",
      "Middleware and API gateway implementation",
      "Cross-domain solution integration",
      "End-to-end integration testing and certification",
    ],
    focusAreas: [
      "System-of-systems integration",
      "API and middleware development",
      "Data exchange and interoperability",
      "Cross-domain solutions",
      "Legacy modernization",
    ],
    frameworks: ["NIST Interoperability Standards", "DoD Net-Centric Data Strategy", "JCIDS", "SOA Reference Architecture"],
  },
  {
    slug: "it-operations-management",
    title: "IT Operations Management",
    shortTitle: "IT Operations",
    icon: Server,
    description:
      "Managing and sustaining IT infrastructure, service desks, and operational environments.",
    overview:
      "RCG ensures mission-critical IT environments remain available, performant, and secure. From service desk operations to infrastructure monitoring, our teams keep the backbone of government operations running 24/7.",
    approach: [
      "ITIL-based service management implementation",
      "Proactive monitoring and incident response",
      "Capacity planning and performance optimization",
      "Continuous improvement through metrics and SLAs",
    ],
    focusAreas: [
      "Infrastructure management",
      "Service desk operations",
      "Configuration management",
      "Incident and problem management",
      "Release and deployment management",
    ],
    frameworks: ["ITIL v4", "NIST SP 800-53", "FedRAMP", "ISO 20000", "DISA STIGs"],
  },
  {
    slug: "cybersecurity-policy",
    title: "Cybersecurity Policy",
    shortTitle: "Cybersecurity",
    icon: ShieldCheck,
    description:
      "Developing and implementing cybersecurity frameworks, RMF, and compliance programs.",
    overview:
      "RCG develops and implements comprehensive cybersecurity policies and programs that protect government systems and data. We guide agencies through the Risk Management Framework and ensure compliance with evolving federal cybersecurity mandates.",
    approach: [
      "RMF assessment and authorization support",
      "Security control implementation and assessment",
      "Continuous monitoring strategy development",
      "Policy development and compliance gap analysis",
    ],
    focusAreas: [
      "Risk Management Framework (RMF)",
      "Security authorization (ATO)",
      "Continuous monitoring",
      "Vulnerability management",
      "Zero Trust implementation",
    ],
    frameworks: ["NIST RMF", "NIST CSF", "CISA Zero Trust Maturity Model", "CMMC", "FISMA"],
  },
  {
    slug: "risk-management",
    title: "Risk Management",
    shortTitle: "Risk",
    icon: AlertTriangle,
    description:
      "Identifying, assessing, and mitigating programmatic and technical risks across the lifecycle.",
    overview:
      "RCG applies disciplined risk management practices to help government programs anticipate challenges, allocate resources effectively, and make informed decisions. Our approach transforms uncertainty into actionable intelligence for program leadership.",
    approach: [
      "Risk identification through structured analysis",
      "Quantitative and qualitative risk assessment",
      "Mitigation strategy development and tracking",
      "Risk reporting and executive dashboards",
    ],
    focusAreas: [
      "Programmatic risk analysis",
      "Technical risk assessment",
      "Schedule and cost risk modeling",
      "Risk register management",
      "Opportunity management",
    ],
    frameworks: ["DoD Risk Management Guide", "ISO 31000", "PMI Risk Management Standard", "GAO Cost Estimating Guide"],
  },
  {
    slug: "acquisition-contract-management",
    title: "Acquisition & Contract Management",
    shortTitle: "Acquisition",
    icon: FileSignature,
    description:
      "Supporting the full acquisition lifecycle from requirements definition to contract closeout.",
    overview:
      "RCG supports government acquisition professionals throughout the procurement lifecycle—from market research and requirements definition through source selection, contract execution, and closeout. Our team ensures compliance with FAR/DFARS while maximizing mission value.",
    approach: [
      "Acquisition strategy and planning support",
      "Market research and source selection assistance",
      "Contract performance monitoring and reporting",
      "Closeout documentation and lessons learned",
    ],
    focusAreas: [
      "Acquisition planning and strategy",
      "Source selection support",
      "Contract administration",
      "Performance-based contracting",
      "Small business program support",
    ],
    frameworks: ["FAR/DFARS", "DoD 5000 Series", "FITARA", "Category Management", "OMB Circular A-123"],
  },
  {
    slug: "project-management",
    title: "Project Management",
    shortTitle: "Project Mgmt",
    icon: FolderKanban,
    description:
      "Planning, executing, and controlling projects to deliver on time, within scope, and on budget.",
    overview:
      "RCG delivers disciplined project management that keeps government programs on track. Our certified project managers apply proven methodologies to plan, execute, monitor, and close projects—ensuring stakeholder alignment, risk mitigation, and measurable outcomes at every milestone.",
    approach: [
      "Project planning with defined scope, schedule, and budget baselines",
      "Earned value management and performance reporting",
      "Stakeholder communication and change control",
      "Lessons learned capture and continuous process improvement",
    ],
    focusAreas: [
      "Schedule and cost management",
      "Earned value analysis",
      "Stakeholder engagement",
      "Integrated master scheduling",
      "Program reviews and gate milestones",
    ],
    frameworks: ["PMI PMBOK", "DoD 5000 Series", "EVM (ANSI/EIA-748)", "ISO 21500", "Agile/Hybrid Methodologies"],
  },
];

export const getServiceBySlug = (slug: string): ServiceData | undefined =>
  services.find((s) => s.slug === slug);
